/**
 * Tipos primitivos do contrato entre API e front.
 *
 * O mesmo schema valida a entrada no servidor e o formulário no navegador — não
 * existe "validação do front" separada que possa divergir da regra real.
 */

import { isValidHex, isValidPhone, normalizePhone } from '@barbearia/core';
import { z } from 'zod';

export const idSchema = z.string().min(1, 'Identificador obrigatório');

export const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato AAAA-MM-DD');

export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-4]):[0-5]\d$/, 'Horário deve estar no formato HH:mm');

/** Instante em ISO 8601. O servidor sempre trabalha em UTC. */
export const instantSchema = z.iso.datetime({ offset: true }).or(z.iso.datetime());

/**
 * Data de nascimento: data de calendário, nunca instante.
 *
 * O limite superior é hoje mais um dia, e a folga é de propósito — o fuso de
 * quem preenche pode estar à frente do fuso do servidor, e recusar o
 * aniversário de alguém por causa disso seria um bug impossível de explicar.
 */
export const birthDateSchema = calendarDateSchema
  .refine((value) => {
    const year = Number(value.slice(0, 4));
    return year >= 1900;
  }, 'Data de nascimento improvável')
  .refine((value) => {
    const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
    return value <= tomorrow;
  }, 'Data de nascimento no futuro');

/**
 * Telefone entra em qualquer formato e sai em E.164 — é assim que a ficha que o
 * barbeiro criou no balcão encontra a conta que o cliente criou pelo link.
 */
export const phoneSchema = z
  .string()
  .trim()
  .refine((value) => isValidPhone(value), 'Telefone inválido. Informe DDD e número.')
  .transform((value) => normalizePhone(value));

/**
 * Booleano vindo da query string.
 *
 * `z.coerce.boolean()` não serve: ele faz `Boolean(valor)`, e `Boolean('false')`
 * é `true` — o parâmetro desligado chega ligado no servidor. Foi assim que
 * "Ver arquivados" nasceu sempre ligado, mostrando cliente arquivado na lista
 * de quem não pediu para vê-los.
 */
export const queryBooleanSchema = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value) => value === true || value === 'true' || value === '1');

export const hexColorSchema = z
  .string()
  .trim()
  .refine((value) => isValidHex(value), 'Cor deve estar no formato #RRGGBB');

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Endereço muito curto')
  .max(40, 'Endereço muito longo')
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use apenas letras, números e hífens');

/** Subdomínios que a plataforma usa e nenhuma barbearia pode tomar. */
export const RESERVED_SLUGS = [
  'api',
  'admin',
  'app',
  'www',
  'static',
  'assets',
  'media',
  'mail',
  'ftp',
] as const;

export const availableSlugSchema = slugSchema.refine(
  (value) => !(RESERVED_SLUGS as readonly string[]).includes(value),
  'Este endereço é reservado pela plataforma',
);

export const paginationSchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export type Pagination = z.infer<typeof paginationSchema>;

/** Formato único de erro da API — o front trata um só formato. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    /** Erros por campo, quando a falha é de validação. */
    fields?: Record<string, string[]>;
  };
}

export const API_ERROR_CODES = {
  VALIDATION: 'VALIDATION',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
  TENANT_SUSPENDED: 'TENANT_SUSPENDED',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL: 'INTERNAL',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];
